import React, { useEffect, useMemo, useState } from 'react';
import { apiCall } from '../../../../services/realApi';
import { Modal, Input, Button, Select } from '../../../../components/ui';
import { useAuth } from '../../../../contexts/AuthContext';
import { MODULES } from '../../../../utils/permissions';
import { FiChevronDown as ChevronDown, FiChevronUp as ChevronUp } from 'react-icons/fi';
import AddUserModal from './AddUserModal';
import InviteUserModal from './InviteUserModal';

const ROLE_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'partner', label: 'Partner' },
  { value: 'aws', label: 'AWS' },
  { value: 'aivilo_staff', label: 'Aivilo Staff' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const MODULE_LABELS = {
  [MODULES.HUB]: 'Hub',
  [MODULES.PLATFORMOPS]: 'CloudOps',
  [MODULES.DATAOPS]: 'DataOps',
  [MODULES.GENAIOPS]: 'GenAIOps',
  [MODULES.AGENTOPS]: 'AgentOps'
};

const PERMISSION_MODULE_ORDER = [
  MODULES.HUB,
  MODULES.PLATFORMOPS,
  MODULES.DATAOPS,
  MODULES.GENAIOPS,
  MODULES.AGENTOPS
];

interface AdminPermission {
  id: string;
  name: string;
  description?: string;
}

interface AdminUser {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  organization?: string;
  lastLogin?: string;
  partnerId?: string;
  clientId?: string;
}

interface AdminRole {
  id: string;
  name: string;
}

interface AdminClient {
  id: string;
  name: string;
}

interface AdminPartner {
  id: string;
  name: string;
}

const getPermissionModule = (permission: AdminPermission | string) => {
  const name = (typeof permission === 'string' ? permission : permission?.name) || '';
  const resource = name.split('.')[0];

  switch (resource) {
    case 'platformops':
      return MODULES.PLATFORMOPS;
    case 'dataops':
      return MODULES.DATAOPS;
    case 'genaiops':
      return MODULES.GENAIOPS;
    case 'agentops':
      return MODULES.AGENTOPS;
    default:
      return MODULES.HUB;
  }
};

interface AdminUsersTabProps {
  onRefresh?: () => void;
}

const AdminUsersTab: React.FC<AdminUsersTabProps> = ({ onRefresh }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [allPermissions, setAllPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPermissionsSection, setShowPermissionsSection] = useState(false);
  const [expandedPermissionModules, setExpandedPermissionModules] = useState<Record<string, boolean>>({});

  // Group permissions by module
  const groupedPermissions = useMemo(() => {
    return allPermissions.reduce<Record<string, AdminPermission[]>>((acc, permission) => {
      const moduleKey = getPermissionModule(permission);
      if (!acc[moduleKey]) {
        acc[moduleKey] = [];
      }
      acc[moduleKey].push(permission);
      return acc;
    }, {});
  }, [allPermissions]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall('/users');
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      console.error('Error loading users:', err);
      setError((err instanceof Error ? err.message : null) || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await apiCall('/roles');
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading roles:', err);
      setRoles([]);
    }
  };

  const loadClients = async () => {
    try {
      const data = await apiCall('/clients');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    }
  };

  const loadPartners = async () => {
    try {
      const data = await apiCall('/partners');
      setPartners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading partners:', err);
      setPartners([]);
    }
  };

  const loadAllPermissions = async () => {
    try {
      const data = await apiCall('/roles/permissions');
      setAllPermissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setAllPermissions([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadClients();
    loadPartners();
    loadAllPermissions();
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const search = searchTerm.trim().toLowerCase();
      if (search) {
        const name = (u.name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        if (!name.includes(search) && !email.includes(search)) {
          return false;
        }
      }
      if (roleFilter !== 'all' && u.role !== roleFilter) {
        return false;
      }
      if (statusFilter !== 'all') {
        const isActive = u.isActive !== false;
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;
      }
      return true;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const openEditModal = async (user: AdminUser) => {
    setSelectedUser(user);
    setSelectedRoleIds([]);
    setSelectedPermissionIds([]);
    setShowPermissionsSection(false);
    setExpandedPermissionModules({});
    setShowEditModal(true);

    // Load user's roles
    try {
      const userRoles = await apiCall(`/users/${user.id}/roles`);
      const roleIds = Array.isArray(userRoles)
        ? userRoles.map((r) => r.role_id).filter(Boolean)
        : [];
      setSelectedRoleIds(roleIds);
    } catch (err) {
      console.error('Error loading user roles:', err);
      setSelectedRoleIds([]);
    }

    // Load user's direct permissions
    try {
      const userPermissions = await apiCall(`/users/${user.id}/permissions`);
      const permissionIds = Array.isArray(userPermissions)
        ? userPermissions.map((p) => p.id).filter(Boolean)
        : [];
      setSelectedPermissionIds(permissionIds);
    } catch (err) {
      console.error('Error loading user permissions:', err);
      setSelectedPermissionIds([]);
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) => {
      if (prev.includes(roleId)) {
        return prev.filter((id) => id !== roleId);
      }
      return [...prev, roleId];
    });
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissionIds((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      }
      return [...prev, permissionId];
    });
  };

  const handleSelectAllPermissions = () => {
    const allIds = allPermissions.map((p) => p.id);
    setSelectedPermissionIds(allIds);
  };

  const handleDeselectAllPermissions = () => {
    setSelectedPermissionIds([]);
  };

  const togglePermissionModule = (moduleKey: string) => {
    setExpandedPermissionModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      // Update user details
      const payload = {
        name: selectedUser.name || null,
        email: selectedUser.email || null,
        role: selectedUser.role,
        is_active: selectedUser.isActive,
        partner_id: selectedUser.partnerId || null,
        client_id: selectedUser.clientId || null,
      };
      await apiCall(`/users/${selectedUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      // Update user roles
      await apiCall(`/users/${selectedUser.id}/roles`, {
        method: 'PUT',
        body: JSON.stringify({ role_ids: selectedRoleIds }),
      });

      // Update user direct permissions
      await apiCall(`/users/${selectedUser.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permission_ids: selectedPermissionIds }),
      });

      setShowEditModal(false);
      setSelectedUser(null);
      if (onRefresh) {
        onRefresh();
      }
      loadUsers();
    } catch (err: unknown) {
      console.error('Error saving user:', err);
      alert((err instanceof Error ? err.message : null) || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiCall(`/users/${userId}`, { method: 'DELETE' });
      loadUsers();
    } catch (err: unknown) {
      console.error('Error deleting user:', err);
      alert((err instanceof Error ? err.message : null) || 'Failed to delete user');
    }
  };

  const handleClose = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setSelectedRoleIds([]);
    setSelectedPermissionIds([]);
    setShowPermissionsSection(false);
    setExpandedPermissionModules({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
          <Input
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name or email"
          />
          <Select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            options={[{ value: 'all', label: 'All' }, ...ROLE_OPTIONS]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setShowAddUserModal(true)}>
            Add User
          </Button>
          <Button variant="primary" onClick={() => setShowInviteModal(true)}>
            Invite AWS User
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Organization</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Active</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{u.name || 'Unknown User'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{u.role}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${u.isActive === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {u.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{u.organization || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4 text-right text-sm">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => openEditModal(u)}>
                        Edit
                      </Button>
                      {currentUser?.user_id !== u.id && (
                        <Button variant="danger" onClick={() => handleDelete(u.id)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <Modal isOpen={showEditModal} onClose={handleClose} title="Edit User" maxWidth="max-w-3xl">
          <div className="space-y-4">
            <Input
              label="Name"
              value={selectedUser.name || ''}
              onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, name: e.target.value }) : null)}
            />
            <Input
              label="Email"
              value={selectedUser.email || ''}
              onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, email: e.target.value }) : null)}
            />
            <Select
              label="Legacy Role"
              value={selectedUser.role}
              onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, role: e.target.value }) : null)}
              options={ROLE_OPTIONS}
            />
            <Select
              label="Status"
              value={selectedUser.isActive === false ? 'inactive' : 'active'}
              onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, isActive: e.target.value === 'active' }) : null)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />

            {selectedUser.role === 'partner' && (
              <Select
                label="Partner"
                value={selectedUser.partnerId || ''}
                onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, partnerId: e.target.value }) : null)}
                options={partners.map((p) => ({ value: p.id, label: p.name }))}
              />
            )}

            {selectedUser.role === 'client' && (
              <Select
                label="Client"
                value={selectedUser.clientId || ''}
                onChange={(e) => setSelectedUser((prev) => prev ? ({ ...prev, clientId: e.target.value }) : null)}
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}

            {/* RBAC Roles Section */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">RBAC Roles</p>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                {roles.map((role) => (
                  <label key={role.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{role.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Direct Permissions Section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <button
                type="button"
                onClick={() => setShowPermissionsSection(!showPermissionsSection)}
                className="w-full flex items-center justify-between text-left"
              >
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Direct Permissions
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {selectedPermissionIds.length} of {allPermissions.length} selected
                    <span className="ml-2 text-blue-600 dark:text-blue-400">
                      (additional permissions not from roles)
                    </span>
                  </p>
                </div>
                {showPermissionsSection ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {showPermissionsSection && (
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      Grant permissions directly to user (in addition to role permissions)
                    </span>
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={handleSelectAllPermissions}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleDeselectAllPermissions}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {PERMISSION_MODULE_ORDER.map((moduleKey) => {
                      const modulePermissions = groupedPermissions[moduleKey] || [];
                      if (modulePermissions.length === 0) {
                        return null;
                      }

                      const selectedCount = modulePermissions.filter(permission =>
                        selectedPermissionIds.includes(permission.id)
                      ).length;

                      return (
                        <div key={moduleKey} className="border border-gray-200 dark:border-gray-700 rounded-md">
                          <button
                            type="button"
                            onClick={() => togglePermissionModule(moduleKey)}
                            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {MODULE_LABELS[moduleKey] || moduleKey}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                  {selectedCount} of {modulePermissions.length}
                                </span>
                              </div>
                            </div>
                            {expandedPermissionModules[moduleKey] ? (
                              <ChevronUp className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            )}
                          </button>

                          {expandedPermissionModules[moduleKey] && (
                            <div className="px-2 pb-2 space-y-1">
                              {modulePermissions.map((permission) => (
                                <label
                                  key={permission.id}
                                  className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissionIds.includes(permission.id)}
                                    onChange={() => handlePermissionToggle(permission.id)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center flex-wrap gap-x-2">
                                      <span className="text-sm text-gray-900 dark:text-white">
                                        {permission.name}
                                      </span>
                                    </div>
                                    {permission.description && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {permission.description}
                                      </p>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
                Save Changes
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showAddUserModal && (
        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={() => {
            setShowAddUserModal(false);
            loadUsers();
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {showInviteModal && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            loadUsers();
            if (onRefresh) onRefresh();
          }}
          allUsers={users.map(u => ({ user_id: u.id, name: u.name, email: u.email }))}
          inviteContext={null}
          currentUserId={currentUser?.id || currentUser?.user_id}
        />
      )}
    </div>
  );
};

export default AdminUsersTab;
